/**
 * Adobe Acrobat: PDF edit, convert, sign tools
 *
 * The most-installed extension in the Chrome Web Store (~312M users), and an
 * unusually easy target. Its manifest declares ~40 web-accessible resources with
 * `"matches": ["<all_urls>"]` and no `use_dynamic_url`, so any page on the web can
 * fetch them directly at a stable, predictable URL.
 *
 * It also declares a second, smaller WAR group *with* `use_dynamic_url: true`. Those
 * resources get a rotating URL token and are not reachable — we probe one of them as a
 * control, to show the mitigation working side-by-side with the leak.
 *
 * Manifest verified against version 26.7.1.1 (July 2026).
 */

import { probeFetch, probeImage } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'efaidnbmnnnibpcajpcglclefindmkaj';

export const adobeAcrobat: ExtensionDefinition = {
  id: ID,
  name: 'Adobe Acrobat: PDF edit, convert, sign tools',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 312_000_000,
  notes:
    'Exposes ~40 static web-accessible resources to <all_urls>. Detection is ' +
    'essentially conclusive and costs a single request.',

  signals: [
    {
      id: 'war-viewer-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch viewer.html — the extension\'s bundled PDF viewer, exposed to every origin.',
      weight: 1,
      run: () => probeFetch(ID, 'viewer.html'),
    },
    {
      id: 'war-extension-detection-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch extension-detection.html — a page Adobe ships specifically so its own ' +
        'sites can detect the extension. It is exposed to <all_urls>, so anyone can use it.',
      weight: 1,
      run: () => probeFetch(ID, 'extension-detection.html'),
    },
    {
      id: 'war-spectrum-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch dist/spectrum.js — a bundled copy of Adobe\'s Spectrum design system. ' +
        'Corroborates the identification and would survive the HTML entry points being removed.',
      weight: 0.5,
      run: () => probeFetch(ID, 'dist/spectrum.js'),
    },
    {
      id: 'war-trefoil-image',
      kind: 'web-accessible-resource',
      description:
        'Load the Acrobat logo SVG via <img>. Needs no CORS grant, so it works even ' +
        'where fetch() is blocked by page CSP.',
      weight: 0.5,
      run: () => probeImage(ID, 'browser/images/dc_trefoil_red_32_n.svg'),
    },
    {
      id: 'control-dynamic-url-war',
      kind: 'web-accessible-resource',
      description:
        'CONTROL: fetch browser/js/frameUI.html, declared with use_dynamic_url: true. ' +
        'Its real URL is a rotating per-session token, so this should fail even when ' +
        'the extension IS installed — showing the mitigation Adobe applied to some ' +
        'resources but not most.',
      weight: 0,
      control: true,
      run: () => probeFetch(ID, 'browser/js/frameUI.html'),
    },
  ],
};
